import React, { useEffect, useState } from "react";
import { BarChart3, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { benchmark } from "@/lib/api";
import { formatIDR } from "@/lib/format";

const metrics = [
  { key: "repeat_rate", label: "Repeat Order Rate", suffix: "%", desc: "Persentase pelanggan yang order ulang" },
  { key: "aov", label: "Average Order Value", suffix: "", isMoney: true, desc: "Rata-rata nilai per order" },
  { key: "campaign_conversion", label: "Campaign Conversion", suffix: "%", desc: "Conversion rate campaign marketing" },
  { key: "return_rate", label: "Return Rate", suffix: "%", desc: "Persentase produk yang diretur", lowerIsBetter: true },
  { key: "margin", label: "Net Margin", suffix: "%", desc: "Margin bersih rata-rata" },
];

export default function Benchmark() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { benchmark.get().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-emerald-600" /></div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Benchmark Kategori</h1>
        <p className="text-sm text-gray-500 mt-1">Bandingkan performa Anda dengan rata-rata brand di kategori <span className="font-semibold capitalize">{data?.category?.replace(/_/g, " ")}</span>.</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {metrics.map((m) => {
          const my = data?.merchant?.[m.key] ?? 0;
          const industry = data?.industry?.[m.key] ?? 0;
          const isBetter = m.lowerIsBetter ? my < industry : my > industry;
          const pct = industry > 0 ? Math.min(100, (my / industry) * 100) : 0;
          return (
            <Card key={m.key} className="border-gray-200">
              <CardContent className="p-5">
                <div className="text-xs font-bold uppercase tracking-widest text-gray-500">{m.label}</div>
                <div className="mt-3 flex items-baseline gap-2">
                  <div className="font-heading text-3xl font-bold text-gray-900">
                    {m.isMoney ? formatIDR(my) : `${my}${m.suffix}`}
                  </div>
                  {my > 0 && (
                    <div className={`flex items-center gap-0.5 text-xs font-semibold ${isBetter ? "text-emerald-600" : "text-red-500"}`}>
                      {isBetter ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                      vs rata-rata
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">{m.desc}</p>
                <div className="mt-4 space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-500">Anda</span>
                    <span className="font-semibold text-gray-900">{m.isMoney ? formatIDR(my) : `${my}${m.suffix}`}</span>
                  </div>
                  <Progress value={pct} className="h-2" />
                  <div className="flex items-center justify-between text-xs mt-2">
                    <span className="text-gray-500">Rata-rata Kategori</span>
                    <span className="font-semibold text-gray-500">{m.isMoney ? formatIDR(industry) : `${industry}${m.suffix}`}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-emerald-200 bg-emerald-50/30">
        <CardContent className="p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0"><BarChart3 /></div>
            <div>
              <h3 className="font-heading font-semibold text-gray-900">Insight Kategori</h3>
              <p className="mt-1 text-sm text-gray-700">
                Data benchmark ini berdasarkan agregat brand di kategori <b className="capitalize">{data?.category?.replace(/_/g, " ")}</b>. Untuk data real-time & competitive intelligence, upgrade ke plan <b>Pro</b>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
